import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, LinearProgress, Chip } from '@mui/material';
import { dashboardAPI } from '../services/api';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getComprehensiveDashboard();
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err) {
        setError('Error connecting to backend: ' + err.message);
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const stats = data
    ? [
      { label: 'Total Complaints', value: data.total_complaints?.toString(), icon: '📊' },
      { label: 'Resolved', value: data.resolved?.toString(), icon: '✓' },
      { label: 'In Progress', value: data.in_progress?.toString(), icon: '⏳' },
      { label: 'Resolution Rate', value: data.resolution_rate + '%', icon: '⭐' }
    ]
    : [];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, px: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#1f2937',
            mb: 1
          }}
        >
          Dashboard
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            fontSize: '0.95rem'
          }}
        >
          Overview of complaints and analytics for Balochistan Governance
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3, px: 1 }}>
        {stats.map((stat, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: '#667eea',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#9ca3af', fontWeight: 500, mb: 1 }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#1f2937' }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '2rem' }}>{stat.icon}</Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Satisfaction Section */}
      {data && (
        <>
          <Grid container spacing={2} sx={{ px: 1, mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Satisfaction Metrics
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>Average Rating</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>
                      {data.average_rating}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>Satisfaction Rate</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                      {data.satisfaction_percentage}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Feedback Distribution
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ textAlign: 'center', pb: 2, borderRight: '1px solid #e5e7eb' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>Total Feedback</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {data.satisfied_feedback}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', pb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>Positive Reviews</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                      {data.satisfied}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Status Distribution Card */}
          <Grid container spacing={2} sx={{ px: 1, mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Complaint Status Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {data.status_distribution && data.status_distribution.map((status, idx) => {
                    const statusLabels = { 'rs': 'Resolved', 'ip': 'In Progress', 'dp': 'Dropped' };
                    const statusColors = { 'rs': '#10b981', 'ip': '#f59e0b', 'dp': '#ef4444' };
                    const statusLabel = statusLabels[status.status] || status.status;
                    return (
                      <Box key={idx}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{statusLabel}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: statusColors[status.status] }}>
                            {status.count} ({status.percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={parseFloat(status.percentage)} sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#e5e7eb',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: statusColors[status.status],
                            borderRadius: 4
                          }
                        }} />
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>

            {/* Top Categories Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Top Categories
                </Typography>
                <Box>
                  {(Array.isArray(data.top_categories) ? data.top_categories : data.top_categories?.rows || []).slice(0, 5).map((cat, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, pb: 1.5, borderBottom: idx < 4 ? '1px solid #e5e7eb' : 'none', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{cat.category}</Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          {Math.round((cat.count / data.total_complaints) * 100)}% of total
                        </Typography>
                      </Box>
                      <Chip
                        label={cat.count}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: '600', color: '#667eea', borderColor: '#667eea' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Top Departments & Complaint Types */}
          <Grid container spacing={2} sx={{ px: 1, mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Top Departments by Complaints
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {data.top_departments && (Array.isArray(data.top_departments) ? data.top_departments : data.top_departments?.rows || []).slice(0, 4).map((dept, idx) => (
                    <Box key={idx} sx={{ pb: 1.5, borderBottom: idx < 3 ? '1px solid #e5e7eb' : 'none' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: '70%' }}>
                          {dept.department_name || dept.department}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
                          {dept.total_complaints || dept.complaint_count}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={`${(dept.resolved_count || 0)} Resolved`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', borderColor: '#10b981', color: '#10b981' }}
                        />
                        <Chip
                          label={`${Math.round(dept.resolution_rate || 0)}%`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e0f2fe', color: '#0284c7' }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Complaint Types Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {data.complaint_types && (Array.isArray(data.complaint_types) ? data.complaint_types : data.complaint_types.rows || []).slice(0, 4).map((type, idx) => (
                    <Box key={idx} sx={{ pb: 1.5, borderBottom: idx < 3 ? '1px solid #e5e7eb' : 'none' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {type.complaint_type || type.comp_type || 'Unspecified'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b' }}>
                          {type.total_count || type.total || 0}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(type.total_count || type.total || 0) / data.total_complaints * 100}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#e5e7eb',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#f59e0b',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Monthly Trend Card */}
          <Grid container spacing={2} sx={{ px: 1 }}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Monthly Trend (Last 12 Months)
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1, minHeight: 150 }}>
                  {data.monthly_trend && (Array.isArray(data.monthly_trend) ? data.monthly_trend : data.monthly_trend?.rows || []).slice(-12).map((month, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          bgcolor: '#667eea',
                          borderRadius: '4px 4px 0 0',
                          height: `${(month.total_complaints / Math.max(...(Array.isArray(data.monthly_trend) ? data.monthly_trend : data.monthly_trend?.rows || []).map(m => m.total_complaints))) * 120}px`,
                          transition: 'background-color 0.3s',
                          '&:hover': {
                            bgcolor: '#5568d3'
                          },
                          position: 'relative'
                        }}
                        title={`${month.month}: ${month.total_complaints} complaints`}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          color: '#6b7280',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%'
                        }}
                      >
                        {month.month.split('-')[1]}/{month.month.split('-')[0]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
